import type { FinishZoneId, FrameColourOption, FrameFinishOption, FrameFinishSelections } from '@/types'

/**
 * Frame finish configurator — zones and the house finish library.
 *
 * The 3D web model (built by scripts/build-frame-model.mjs) carries exactly one
 * material per zone, named ZONE_LUGS / ZONE_CARBON_TUBES / ZONE_LINKS /
 * ZONE_BOLTS. The viewer finds meshes by those material names and swaps in a
 * material built from the selected FrameFinishOption.
 *
 * Riders consume this catalog through BuilderContext (finishCatalog), which
 * layers CMS overrides and additions on top — never import this directly in
 * builder components.
 */

export interface FinishZoneDefinition {
  id: FinishZoneId
  label: string
  description: string
  /** Material name inside the web GLB that this zone recolours. */
  materialName: string
}

export const FINISH_ZONES: FinishZoneDefinition[] = [
  {
    id: 'lugs',
    label: 'Lugs',
    description: 'Additive-manufactured titanium nodes joining the tubes.',
    materialName: 'ZONE_LUGS',
  },
  {
    id: 'tubes',
    label: 'Carbon Tubes',
    description: 'Filament-wound carbon tube set.',
    materialName: 'ZONE_CARBON_TUBES',
  },
  {
    id: 'links',
    label: 'Linkage',
    description: 'Machined suspension links and adjustable hardware.',
    materialName: 'ZONE_LINKS',
  },
  {
    id: 'bolts',
    label: 'Bolts',
    description: 'Pivot hardware, axles, and fasteners.',
    materialName: 'ZONE_BOLTS',
  },
  {
    id: 'badge',
    label: 'Head Badge',
    description: 'The marque badge set into the head tube.',
    materialName: 'ZONE_BADGE',
  },
]

/** Per-frame web viewer model. Frames without an entry fall back to swatch-only UI. */
export const FRAME_MODEL_URLS: Record<string, string> = {
  'raptor-i': '/models/tuatara-frame-v1.glb',
}

export const FRAME_FINISHES: FrameFinishOption[] = [
  // ── Lugs ────────────────────────────────────────────────────────────────────
  {
    id: 'lugs-sandblasted-titanium',
    zone: 'lugs',
    label: 'Sandblasted Titanium',
    description: 'Uniform matte grain straight off the media blaster. The house lug finish.',
    hex: '#B9BBB4',
    metalness: 0.7,
    roughness: 0.72,
    clearcoat: 0,
    pattern: 'sandblasted',
    patternScale: 10,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'lugs-raw-titanium',
    zone: 'lugs',
    label: 'Raw Titanium',
    description: 'As-printed and machined surface, sealed but untreated.',
    hex: '#A8AAA2',
    metalness: 1,
    roughness: 0.55,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'lugs-brushed-titanium',
    zone: 'lugs',
    label: 'Brushed Titanium',
    description: 'Directional satin brush, hand-finished along each lug.',
    hex: '#B0B2AA',
    metalness: 1,
    roughness: 0.45,
    clearcoat: 0,
    pattern: 'brushed',
    patternScale: 6,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'lugs-polished-titanium',
    zone: 'lugs',
    label: 'Polished Titanium',
    description: 'Mirror-bright hand polish. Shows every reflection.',
    hex: '#D8DAD4',
    metalness: 1,
    roughness: 0.12,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'lugs-gloss-black',
    zone: 'lugs',
    label: 'Gloss Black',
    description: 'Deep wet-look black over the printed surface.',
    hex: '#0C0C0C',
    metalness: 0,
    roughness: 0.25,
    clearcoat: 1,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'lugs-matte-black',
    zone: 'lugs',
    label: 'Matte Black',
    description: 'Stealth ceramic-matte black.',
    hex: '#121212',
    metalness: 0,
    roughness: 0.85,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'lugs-metallic-gold',
    zone: 'lugs',
    label: 'Metallic Gold',
    description: 'Burton gold, matched to the marque.',
    hex: '#C9A84C',
    metalness: 0.9,
    roughness: 0.35,
    clearcoat: 0.6,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'lugs-silver-white',
    zone: 'lugs',
    label: 'Silver White',
    description: 'Bright metallic silver-white.',
    hex: '#E4E4E0',
    metalness: 0.7,
    roughness: 0.4,
    clearcoat: 0.4,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },

  // ── Carbon tubes ────────────────────────────────────────────────────────────
  {
    id: 'tubes-gloss-3k-twill',
    zone: 'tubes',
    label: 'Gloss 3K Twill',
    description: 'Classic 3K twill weave under a gloss clear coat. The house tube finish.',
    hex: '#2A2E33',
    metalness: 0,
    roughness: 0.3,
    clearcoat: 1,
    pattern: 'twill',
    patternScale: 5,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'tubes-matte-3k-twill',
    zone: 'tubes',
    label: 'Matte 3K Twill',
    description: '3K twill weave with a satin-matte clear.',
    hex: '#26292D',
    metalness: 0,
    roughness: 0.65,
    clearcoat: 0.15,
    pattern: 'twill',
    patternScale: 5,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'tubes-ud-matte',
    zone: 'tubes',
    label: 'UD Carbon Matte',
    description: 'Unidirectional fibre, subtle and technical.',
    hex: '#232629',
    metalness: 0,
    roughness: 0.7,
    clearcoat: 0.1,
    pattern: 'ud',
    patternScale: 10,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'tubes-ud-gloss',
    zone: 'tubes',
    label: 'UD Carbon Gloss',
    description: 'Unidirectional fibre under a deep gloss clear.',
    hex: '#26292D',
    metalness: 0,
    roughness: 0.28,
    clearcoat: 1,
    pattern: 'ud',
    patternScale: 10,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'tubes-forged-carbon',
    zone: 'tubes',
    label: 'Forged Carbon',
    description: 'Chopped-tow forged texture — every tube one of one.',
    hex: '#2B2D30',
    metalness: 0,
    roughness: 0.45,
    clearcoat: 0.8,
    pattern: 'forged',
    patternScale: 3,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'tubes-smoke-tint',
    zone: 'tubes',
    label: 'Smoke Tint Clear',
    description: 'Tinted smoke lacquer over the raw weave.',
    hex: '#3A3E42',
    metalness: 0,
    roughness: 0.32,
    clearcoat: 1,
    pattern: 'twill',
    patternScale: 5,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'tubes-painted-black',
    zone: 'tubes',
    label: 'Painted Black',
    description: 'Solid gloss black paint over the tube set.',
    hex: '#0C0C0C',
    metalness: 0,
    roughness: 0.3,
    clearcoat: 0.9,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'tubes-painted-white',
    zone: 'tubes',
    label: 'Painted White',
    description: 'Solid pearl white paint over the tube set.',
    hex: '#E8E8E4',
    metalness: 0.1,
    roughness: 0.3,
    clearcoat: 0.9,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },

  // ── Linkage ─────────────────────────────────────────────────────────────────
  {
    id: 'links-black-anodised',
    zone: 'links',
    label: 'Black Anodised',
    description: 'Type II black anodise on the machined links. The house link finish.',
    hex: '#161616',
    metalness: 0.85,
    roughness: 0.5,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'links-raw-machined',
    zone: 'links',
    label: 'Raw Machined',
    description: 'Bare 7075 straight off the mill, clear sealed.',
    hex: '#C4C6C2',
    metalness: 1,
    roughness: 0.35,
    clearcoat: 0,
    pattern: 'brushed',
    patternScale: 8,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'links-silver-anodised',
    zone: 'links',
    label: 'Silver Anodised',
    description: 'Clear anodise, bright and hard-wearing.',
    hex: '#D0D2CE',
    metalness: 0.9,
    roughness: 0.42,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'links-gold-anodised',
    zone: 'links',
    label: 'Gold Anodised',
    description: 'Classic gold anodise, matched to the Burton palette.',
    hex: '#C9A84C',
    metalness: 0.9,
    roughness: 0.4,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'links-titanium-grey',
    zone: 'links',
    label: 'Titanium Grey',
    description: 'Grey anodise matched to the raw lug tone.',
    hex: '#848680',
    metalness: 0.9,
    roughness: 0.5,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'links-gloss-black',
    zone: 'links',
    label: 'Gloss Black',
    description: 'Wet-look black over the machined surfaces.',
    hex: '#0C0C0C',
    metalness: 0.2,
    roughness: 0.22,
    clearcoat: 1,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },

  // ── Bolts ───────────────────────────────────────────────────────────────────
  {
    id: 'bolts-raw-titanium',
    zone: 'bolts',
    label: 'Raw Titanium',
    description: 'Grade 5 titanium hardware, raw finish. The house bolt kit.',
    hex: '#A4A6A0',
    metalness: 1,
    roughness: 0.45,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'bolts-black',
    zone: 'bolts',
    label: 'Black',
    description: 'Black-oxide titanium hardware.',
    hex: '#141414',
    metalness: 0.9,
    roughness: 0.5,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'bolts-gold',
    zone: 'bolts',
    label: 'Gold',
    description: 'Gold PVD-coated titanium hardware.',
    hex: '#C9A84C',
    metalness: 1,
    roughness: 0.32,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'bolts-silver',
    zone: 'bolts',
    label: 'Silver',
    description: 'Bright polished titanium hardware.',
    hex: '#D8DAD4',
    metalness: 1,
    roughness: 0.2,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'bolts-bronze',
    zone: 'bolts',
    label: 'Bronze',
    description: 'Warm bronze PVD over titanium.',
    hex: '#9C6B3E',
    metalness: 1,
    roughness: 0.38,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },

  // ── Head badge ──────────────────────────────────────────────────────────────
  {
    id: 'badge-gold',
    zone: 'badge',
    label: 'Gold',
    description: 'Burton gold, matched to the marque. The house badge finish.',
    hex: '#C9A84C',
    metalness: 1,
    roughness: 0.3,
    clearcoat: 0.4,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'badge-silver',
    zone: 'badge',
    label: 'Silver',
    description: 'Bright polished silver marque.',
    hex: '#D8DAD4',
    metalness: 1,
    roughness: 0.18,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'badge-gloss-black',
    zone: 'badge',
    label: 'Gloss Black',
    description: 'Deep wet-look black enamel badge.',
    hex: '#0C0C0C',
    metalness: 0.2,
    roughness: 0.22,
    clearcoat: 1,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'badge-green',
    zone: 'badge',
    label: 'Deep Green',
    description: 'Burton green enamel, matched to the marque.',
    hex: '#1A3A2A',
    metalness: 0.3,
    roughness: 0.3,
    clearcoat: 0.8,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
  {
    id: 'badge-titanium',
    zone: 'badge',
    label: 'Raw Titanium',
    description: 'Bead-blasted raw titanium marque.',
    hex: '#A8AAA2',
    metalness: 1,
    roughness: 0.5,
    clearcoat: 0,
    pattern: 'none',
    patternScale: 1,
    priceDelta: 0,
    enabled: true,
  },
]

/** House finish direction — the default selection per zone. */
export const DEFAULT_FINISH_SELECTIONS: FrameFinishSelections = {
  lugs: 'lugs-sandblasted-titanium',
  tubes: 'tubes-gloss-3k-twill',
  links: 'links-black-anodised',
  bolts: 'bolts-raw-titanium',
  badge: 'badge-gold',
}

export function finishesForZone(catalog: FrameFinishOption[], zone: FinishZoneId): FrameFinishOption[] {
  return catalog.filter((option) => option.zone === zone && option.enabled)
}

export function getFinishOption(catalog: FrameFinishOption[], id: string): FrameFinishOption | undefined {
  return catalog.find((option) => option.id === id)
}

export function carbonTubePatternOptions(catalog: FrameFinishOption[]): FrameFinishOption[] {
  const tubeOptions = finishesForZone(catalog, 'tubes')
  const patterned = tubeOptions.filter((option) => option.pattern !== 'none' || Boolean(option.textureUrl))
  return patterned.length > 0 ? patterned : tubeOptions
}

export function defaultCarbonTubePatternId(catalog: FrameFinishOption[]): string {
  return carbonTubePatternOptions(catalog)[0]?.id ?? DEFAULT_FINISH_SELECTIONS.tubes
}

export function defaultCarbonTubeTintId(tints: FrameColourOption[]): string {
  return tints[0]?.id ?? 'clear'
}

export function applyCarbonTubeTint(
  catalog: FrameFinishOption[],
  patternId: string,
  tint: FrameColourOption | undefined,
): FrameFinishOption[] {
  if (!tint) return catalog
  return catalog.map((option) =>
    option.id === patternId && option.zone === 'tubes' && (option.pattern !== 'none' || option.textureUrl)
      ? {
          ...option,
          hex: tint.hex,
          label: option.label,
          description: `${option.description} Tint: ${tint.label}.`,
        }
      : option,
  )
}

/**
 * Resolve the selected option per zone, falling back to the house default and
 * then to the first enabled option so the viewer never renders an empty zone.
 */
export function resolveFinishSelections(
  catalog: FrameFinishOption[],
  selections: Partial<FrameFinishSelections> | null | undefined,
): Record<FinishZoneId, FrameFinishOption | null> {
  return Object.fromEntries(
    FINISH_ZONES.map((zone) => {
      const wanted = selections?.[zone.id]
      const option =
        (wanted ? catalog.find((entry) => entry.id === wanted && entry.zone === zone.id) : undefined) ??
        catalog.find((entry) => entry.id === DEFAULT_FINISH_SELECTIONS[zone.id]) ??
        finishesForZone(catalog, zone.id)[0] ??
        null
      return [zone.id, option]
    }),
  ) as Record<FinishZoneId, FrameFinishOption | null>
}
